// Reveal: a block that grows into place when `show` turns on and dissolves when it turns
// off, staying mounted until the exit finishes so it has something to fade.
// docs/features/page-shell.md, "Reveal"; docs/operations/design-standard.md, "Motion".

import React, { useEffect, useRef, useState } from 'react';
import { MS, useFocusHandoff, usePresence } from './motion';

const Reveal: React.FC<{ show: boolean; children: React.ReactNode }> = ({ show, children }) => {
  const { mounted, open } = usePresence(show, MS.exit);
  // Dissolving with focus inside would leave it on a hidden element, then on the page.
  const root = useFocusHandoff<HTMLDivElement>(!show);
  // What leaves is what was last shown, not whatever the hidden branch would render now.
  const last = useRef(children);
  if (show) last.current = children;
  // Clipped only while it moves. Once settled open, a list that drops out of it, a focus ring
  // at its edge or a glow can spill past it; it clips again the moment it starts to leave.
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (!open) {
      setSettled(false);
      return undefined;
    }
    const t = window.setTimeout(() => setSettled(true), MS.standard);
    return () => window.clearTimeout(t);
  }, [open]);

  if (!mounted) return null;
  return (
    <div
      ref={root}
      aria-hidden={show ? undefined : true}
      className={`grid min-w-0 transition-[grid-template-rows,opacity] motion-reduce:transition-[opacity] ${
        open ? 'grid-rows-[1fr] opacity-100 duration-standard ease-emphasized' : 'grid-rows-[0fr] opacity-0 duration-exit ease-exit'
      }`}
    >
      {/* min-w-0: a grid item is as wide as its widest unbreakable child, so a no-wrap row would widen the page. */}
      <div className={`min-h-0 min-w-0 ${settled ? 'overflow-visible' : 'overflow-hidden'}`}>{last.current}</div>
    </div>
  );
};

export default Reveal;
